import {
  PRESETS,
  applyPreset,
  normaliseSettings,
  type ChaosSettings,
  type PresetId,
} from "./randomizer";

export type RecipePresetId = Exclude<PresetId, "custom">;

export interface RecipeManualState {
  settings: ChaosSettings;
  keepRecipeShape: boolean;
  lastRecipeId: RecipePresetId;
  localRecipeId: string | null;
}

interface HistoryGroup {
  key: string;
  at: number;
}

export interface RecipeManualHistory {
  past: RecipeManualState[];
  present: RecipeManualState;
  future: RecipeManualState[];
  lastGroup: HistoryGroup | null;
}

export const RECIPE_HISTORY_LIMIT = 80;
export const RECIPE_HISTORY_GROUP_MS = 700;

const FALLBACK_RECIPE_ID: RecipePresetId = "pure-chaos";

function isRecipePresetId(value: unknown): value is RecipePresetId {
  return (
    typeof value === "string" &&
    value !== "custom" &&
    PRESETS.some((preset) => preset.id === value)
  );
}

function safeRecipePresetId(value: unknown): RecipePresetId {
  return isRecipePresetId(value) ? value : FALLBACK_RECIPE_ID;
}

export function normaliseRecipeManualState(
  state: RecipeManualState,
): RecipeManualState {
  const settings = normaliseSettings(state.settings);
  const lastRecipeId = safeRecipePresetId(
    settings.presetId !== "custom" ? settings.presetId : state.lastRecipeId,
  );

  return {
    settings,
    keepRecipeShape: Boolean(state.keepRecipeShape),
    lastRecipeId,
    localRecipeId:
      typeof state.localRecipeId === "string" && state.localRecipeId.trim()
        ? state.localRecipeId
        : null,
  };
}

export function createRecipeManualState(
  recipeId: RecipePresetId = FALLBACK_RECIPE_ID,
): RecipeManualState {
  return normaliseRecipeManualState({
    settings: applyPreset(recipeId),
    keepRecipeShape: true,
    lastRecipeId: recipeId,
    localRecipeId: null,
  });
}

export function createRecipeManualHistory(
  initial = createRecipeManualState(),
): RecipeManualHistory {
  return {
    past: [],
    present: normaliseRecipeManualState(initial),
    future: [],
    lastGroup: null,
  };
}

export function recipeManualStatesEqual(
  left: RecipeManualState,
  right: RecipeManualState,
): boolean {
  return (
    left.keepRecipeShape === right.keepRecipeShape &&
    left.lastRecipeId === right.lastRecipeId &&
    left.localRecipeId === right.localRecipeId &&
    left.settings.presetId === right.settings.presetId &&
    left.settings.lengthMin === right.settings.lengthMin &&
    left.settings.lengthMax === right.settings.lengthMax &&
    left.settings.tripletChance === right.settings.tripletChance &&
    left.settings.accentChance === right.settings.accentChance &&
    left.settings.slideChance === right.settings.slideChance &&
    left.settings.tieChance === right.settings.tieChance &&
    left.settings.restChance === right.settings.restChance &&
    left.settings.pitchMin === right.settings.pitchMin &&
    left.settings.pitchMax === right.settings.pitchMax &&
    left.settings.scaleId === right.settings.scaleId &&
    left.settings.root === right.settings.root &&
    left.settings.enforceAudibleStep === right.settings.enforceAudibleStep &&
    left.settings.allowTieOnFirstStep === right.settings.allowTieOnFirstStep
  );
}

export function withPresetSelection(
  state: RecipeManualState,
  recipeId: RecipePresetId,
): RecipeManualState {
  return normaliseRecipeManualState({
    settings: applyPreset(recipeId),
    keepRecipeShape: true,
    lastRecipeId: recipeId,
    localRecipeId: null,
  });
}

export function withLocalRecipeSelection(
  state: RecipeManualState,
  recipe: Pick<
    RecipeManualState,
    "settings" | "keepRecipeShape" | "lastRecipeId" | "localRecipeId"
  >,
): RecipeManualState {
  return normaliseRecipeManualState({
    settings: recipe.settings,
    keepRecipeShape: recipe.keepRecipeShape,
    lastRecipeId: recipe.lastRecipeId,
    localRecipeId: recipe.localRecipeId,
  });
}

export function withoutLocalRecipeLink(
  state: RecipeManualState,
): RecipeManualState {
  return normaliseRecipeManualState({
    ...state,
    localRecipeId: null,
  });
}

function nextManualPresetId(state: RecipeManualState): PresetId {
  if (!state.keepRecipeShape) return "custom";
  return state.settings.presetId !== "custom"
    ? state.settings.presetId
    : state.lastRecipeId;
}

export function withManualSetting<K extends keyof ChaosSettings>(
  state: RecipeManualState,
  key: K,
  value: ChaosSettings[K],
): RecipeManualState {
  return normaliseRecipeManualState({
    ...state,
    settings: {
      ...state.settings,
      presetId: nextManualPresetId(state),
      [key]: value,
    },
  });
}

export function withPitchRange(
  state: RecipeManualState,
  min: number,
  max: number,
): RecipeManualState {
  return normaliseRecipeManualState({
    ...state,
    settings: {
      ...state.settings,
      presetId: nextManualPresetId(state),
      pitchMin: min,
      pitchMax: max,
    },
  });
}

export function withRecipeShape(
  state: RecipeManualState,
  enabled: boolean,
): RecipeManualState {
  if (enabled) {
    return normaliseRecipeManualState({
      ...state,
      keepRecipeShape: true,
      settings: {
        ...state.settings,
        presetId:
          state.settings.presetId !== "custom"
            ? state.settings.presetId
            : state.lastRecipeId,
      },
    });
  }

  return normaliseRecipeManualState({
    ...state,
    keepRecipeShape: false,
    lastRecipeId:
      state.settings.presetId !== "custom"
        ? state.settings.presetId
        : state.lastRecipeId,
    settings: {
      ...state.settings,
      presetId: "custom",
    },
  });
}

function pushPast(
  past: RecipeManualState[],
  current: RecipeManualState,
): RecipeManualState[] {
  const nextPast = [...past, current];
  return nextPast.length > RECIPE_HISTORY_LIMIT
    ? nextPast.slice(nextPast.length - RECIPE_HISTORY_LIMIT)
    : nextPast;
}

function pushFuture(
  future: RecipeManualState[],
  current: RecipeManualState,
): RecipeManualState[] {
  const nextFuture = [current, ...future];
  return nextFuture.slice(0, RECIPE_HISTORY_LIMIT);
}

export function commitRecipeManualState(
  history: RecipeManualHistory,
  updater: (state: RecipeManualState) => RecipeManualState,
  options: { groupKey?: string; now?: number } = {},
): RecipeManualHistory {
  const next = normaliseRecipeManualState(updater(history.present));
  if (recipeManualStatesEqual(history.present, next)) return history;

  const now = options.now ?? Date.now();
  const lastGroup = history.lastGroup;
  const shouldMerge =
    Boolean(options.groupKey) &&
    lastGroup !== null &&
    lastGroup.key === options.groupKey &&
    now - lastGroup.at <= RECIPE_HISTORY_GROUP_MS &&
    history.past.length > 0;

  return {
    past: shouldMerge ? history.past : pushPast(history.past, history.present),
    present: next,
    future: [],
    lastGroup: options.groupKey ? { key: options.groupKey, at: now } : null,
  };
}

export function undoRecipeManualState(
  history: RecipeManualHistory,
): RecipeManualHistory {
  const previous = history.past.at(-1);
  if (!previous) return history;

  return {
    past: history.past.slice(0, -1),
    present: previous,
    future: pushFuture(history.future, history.present),
    lastGroup: null,
  };
}

export function redoRecipeManualState(
  history: RecipeManualHistory,
): RecipeManualHistory {
  const next = history.future[0];
  if (!next) return history;

  return {
    past: pushPast(history.past, history.present),
    present: next,
    future: history.future.slice(1),
    lastGroup: null,
  };
}
